import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Configure axios defaults
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true;

// Options API endpoints
export const optionsApi = {
  // Get ticker information
  getTickerInfo: async (ticker) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/options/tickers/${ticker}/info`);
      return response.data;
    } catch (error) {
      console.error('Error fetching ticker info:', error);
      throw error;
    }
  },

  // Get expiration dates for a ticker
  getExpirations: async (ticker) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/options/tickers/${ticker}/expirations`);
      return response.data;
    } catch (error) {
      console.error('Error fetching expirations:', error);
      throw error;
    }
  },

  // Get options chain
  getOptionsChain: async (ticker, expirationDate, optionType) => {
    try {
      const params = {
        expiration_date: expirationDate,
        option_type: optionType
      };
      
      const response = await axios.get(
        `${API_BASE_URL}/options/tickers/${ticker}/options`, 
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching options chain:', error);
      throw error;
    }
  },
  
  // Get current price of multiple option contracts in batch
  updateContractsBatch: async (contracts) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/options/contracts/batch-update`, contracts);
      
      // If there are errors in the response, they should be captured
      const results = response.data;
      
      // Return the results - the backend should have included any error messages
      return results;
    } catch (error) {
      console.error('Error updating contracts batch:', error);
      
      // Check if the error response includes data about specific contract errors
      if (error.response && error.response.data && error.response.data.detail) {
        console.log('Received error details from API:', error.response.data.detail);
      }
      
      // Return the contracts with error information instead of throwing
      // This allows the UI to preserve existing values for contracts that couldn't be updated
      return contracts.map(contract => ({
        ...contract,
        error: error.message || 'Failed to update contract',
        lastUpdated: new Date().toISOString()
      }));
    }
  },
  
  // Get detailed quote for a specific option contract
  getOptionQuote: async (ticker, contractSymbol, originalSymbol) => {
    try {
      // Use the original Yahoo Finance symbol format if available, otherwise use the display name
      const symbolToUse = originalSymbol || contractSymbol;
      const encodedSymbol = encodeURIComponent(symbolToUse);
      const response = await axios.get(`${API_BASE_URL}/options/contracts/${encodedSymbol}/price`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching option quote for ${contractSymbol}:`, error);
      
      // Check for the specific error message format that indicates an expired contract
      // Check both error.response.data.detail and error.message
      let errorMessage = '';
      
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : JSON.stringify(error.response.data.detail);
        }
      }
      
      if (!errorMessage && error.message) {
        errorMessage = error.message;
      }
      
      console.log(`Error message for ${contractSymbol}:`, errorMessage);
      
      // Check for any of the expired contract error patterns
      if (errorMessage && 
          (errorMessage.includes('cannot be found') || 
           errorMessage.includes('Expiration') || 
           errorMessage.includes('Error calculating greeks'))) {
        
        console.log(`Contract ${contractSymbol} is EXPIRED based on error pattern!`);
        
        // Create a custom error that clearly indicates this is an expired contract
        const expiredError = new Error(errorMessage);
        expiredError.isExpired = true;
        throw expiredError;
      }
      
      // Pass through the original error message which may contain information about expired contracts
      // The error message format from the backend is like:
      // "Error calculating greeks for SPY 05/02 260 CALL: Expiration `2025-05-02` cannot be found. Available expirations are: [...]"
      throw error;
    }
  },

  // Get current price of a single option contract
  getCurrentPrice: async (contractSymbol) => {
    try {
      const encodedSymbol = encodeURIComponent(contractSymbol);
      const response = await axios.get(`${API_BASE_URL}/options/contracts/${encodedSymbol}/price`);
      return response.data;
    } catch (error) {
      console.error('Error fetching current option price:', error);
      throw error;
    }
  },

  // Calculate P&L for a watchlist item
  calculatePnL: async (item) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/options/calculate-pnl`, item);
      return response.data;
    } catch (error) {
      console.error('Error calculating P&L:', error);
      throw error;
    }
  }
};

// Black-Scholes API endpoints
export const blackScholesApi = {
  // Get ticker price
  getTickerPrice: async (ticker) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/black-scholes/ticker/${ticker}/price`);
      // The backend now returns { price, found, ticker } directly
      return response.data;
    } catch (error) {
      console.error('Error fetching ticker price:', error);
      throw error;
    }
  },
  // Calculate both option prices and Greeks
  calculateOptions: async (params) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/black-scholes/calculate`, params);
      return response.data;
    } catch (error) {
      console.error('Error calculating options:', error);
      throw error;
    }
  },

  // Generate sensitivity analysis data
  generateSensitivityData: async (params, variable, rangeMin, rangeMax, steps = 20) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/black-scholes/sensitivity`, 
        { ...params, variable, range_min: rangeMin, range_max: rangeMax, steps }
      );
      return response.data;
    } catch (error) {
      console.error('Error generating sensitivity data:', error);
      throw error;
    }
  },

  // Generate heatmap data
  generateHeatmap: async (params) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/black-scholes/heatmap`, params);
      return response.data;
    } catch (error) {
      console.error('Error generating heatmap data:', error);
      throw error;
    }
  }
};
